import { Add, DeleteOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { usePimoForm } from "@pimo/pimo-components/src/lib/pimo-components/use-pimo-form/use-pimo-form.hook";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Configuration, getDistributionChannels, Project } from "vmd-types";
import {
  ASSESSMENT_CONFIG,
  AUDIT_CONFIG,
  DISTRIBUTION_CONFIG,
  PMO_CONFIG,
  ROLLOUT_CONFIG,
} from "vmd-utils";

export type ReportingUseCasePageProps = {
  configuration?: Configuration;
  projects?: Project[];
  language?: string;
};

export const ReportingUseCasePage: PimoReactComponent<
  ReportingUseCasePageProps,
  "update:view-configuration",
  string
> = ({ language = "en", fireEvent, configuration, projects = [] }) => {
  const { setValue, formState } = usePimoForm<{
    configuration: Configuration;
    enableSaveButton?: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });
  const [selectedUseCase, setSelectedUseCase] = useState(
    configuration?.useCase ?? "distribution"
  );

  const isInitialUseCaseRun = useRef(true);
  useEffect(() => {
    // Map tab to config
    const configMap = {
      audit: AUDIT_CONFIG,
      rollout: ROLLOUT_CONFIG,
      pmo: PMO_CONFIG,
      assessment: ASSESSMENT_CONFIG,
      distribution: DISTRIBUTION_CONFIG,
    } as const;
    if (!selectedUseCase) return;
    const isMount = isInitialUseCaseRun.current;
    isInitialUseCaseRun.current = false;
    // On mount never overwrite an already-persisted config (it has an id, even legacy ones with no useCase);
    // only seed a template when none exists or the user switches tabs.
    if (isMount && formState.configuration?.id) return;
    if (selectedUseCase !== formState.configuration?.useCase) {
      setValue("enableSaveButton", true);
      setValue("configuration", configMap[selectedUseCase]);
      fireEvent?.("update:view-configuration", selectedUseCase);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUseCase]);

  const channelDefs =
    formState.configuration?.distributionChannelDefinition ??
    getDistributionChannels(configuration?.distributionChannelDefinition);

  const updateChannels = (next: { id: string; label: string }[]) => {
    setValue("configuration.distributionChannelDefinition", next);
    setValue("enableSaveButton", true);
  };

  const getChannelUsage = (channelId: string) => {
    const oeCount = projects.filter((p) =>
      p.distributionChannels?.includes(channelId)
    ).length;
    const appCount = projects.reduce(
      (sum, p) =>
        sum +
        (p.reportingItems ?? []).filter(
          (ri) =>
            !ri.isChannelHeader && ri.distributionChannel?.includes(channelId)
        ).length,
      0
    );
    return { oeCount, appCount };
  };

  const [pendingDelete, setPendingDelete] = useState<{
    index: number;
    label: string;
    oeCount: number;
    appCount: number;
  } | null>(null);

  const requestDeleteChannel = (index: number) => {
    const channel = channelDefs[index];
    const { oeCount, appCount } = getChannelUsage(channel.id);
    if (oeCount > 0 || appCount > 0) {
      setPendingDelete({
        index,
        label: channel.label || "this channel",
        oeCount,
        appCount,
      });
    } else {
      updateChannels(channelDefs.filter((_, j) => j !== index));
    }
  };

  const confirmDeleteChannel = () => {
    if (!pendingDelete) return;
    updateChannels(channelDefs.filter((_, j) => j !== pendingDelete.index));
    setPendingDelete(null);
  };

  const renderContent = () => {
    switch (selectedUseCase) {
      case "pmo":
        return (
          <Typography sx={{ fontSize: "14px" }}>
            {getLocalizedMessage(language, "reportingUseCase.pmoDescription") ||
              "In this use case, only Program Management can create findings and define associated measures (mitigations). Once created, the Finding Owner or their team is responsible for regularly reporting progress on each measure. This enables a clear separation of responsibilities while ensuring continuous tracking and resolution of identified risks."}
          </Typography>
        );
      case "rollout":
        return (
          <Typography sx={{ fontSize: "14px" }}>
            {getLocalizedMessage(
              language,
              "reportingUseCase.rolloutDescription"
            ) || "Rollout Status Reporting content goes here."}
          </Typography>
        );
      case "audit":
        return (
          <Typography sx={{ fontSize: "14px" }}>
            {getLocalizedMessage(
              language,
              "reportingUseCase.auditDescription"
            ) ||
              "In this use case, only Program Management can create findings and define associated measures (mitigations). Once created, the Finding Owner or their team is responsible for regularly reporting progress on each measure. This enables a clear separation of responsibilities while ensuring continuous tracking and resolution of identified risks."}
          </Typography>
        );
      case "assessment":
        return (
          <Typography sx={{ fontSize: "14px" }}>
            {getLocalizedMessage(
              language,
              "reportingUseCase.assessmentDescription"
            ) || "Assessment content goes here."}
          </Typography>
        );
      case "distribution":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Typography sx={{ fontSize: "14px" }}>
              {getLocalizedMessage(
                language,
                "reportingUseCase.distributionDescription"
              ) ||
                "Track an OE's distribution channels (Brokers, Agents). Each selected channel holds its own data plus the Apps reported under it."}
            </Typography>
            <Box>
              <Typography sx={{ fontSize: "16px", fontWeight: 500, mb: 1 }}>
                {getLocalizedMessage(
                  language,
                  "reportingUseCase.distributionChannels"
                ) || "Distribution Channels"}
              </Typography>
              <Stack spacing={1}>
                {channelDefs.map((ch, i) => {
                  const { oeCount, appCount } = getChannelUsage(ch.id);
                  const inUse = oeCount > 0 || appCount > 0;
                  return (
                    <Stack
                      direction="row"
                      spacing={1}
                      key={ch.id}
                      alignItems="center"
                    >
                      <TextField
                        size="small"
                        value={ch.label}
                        placeholder="Channel name"
                        onChange={(e) =>
                          updateChannels(
                            channelDefs.map((c, j) =>
                              j === i ? { ...c, label: e.target.value } : c
                            )
                          )
                        }
                        sx={{
                          width: 320,
                          ...{ "& input": { fontSize: "14px" } },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: inUse ? grey[700] : grey[500],
                          minWidth: 150,
                        }}
                      >
                        {inUse
                          ? `Used by ${oeCount} OE${
                              oeCount === 1 ? "" : "s"
                            } · ${appCount} app${appCount === 1 ? "" : "s"}`
                          : "Not in use"}
                      </Typography>
                      <IconButton
                        aria-label="delete channel"
                        onClick={() => requestDeleteChannel(i)}
                      >
                        <DeleteOutlined />
                      </IconButton>
                    </Stack>
                  );
                })}
                <Button
                  startIcon={<Add />}
                  onClick={() =>
                    updateChannels([
                      ...channelDefs,
                      { id: uuidv4(), label: "" },
                    ])
                  }
                  sx={{ textTransform: "none", alignSelf: "flex-start" }}
                >
                  {getLocalizedMessage(
                    language,
                    "reportingUseCase.addChannel"
                  ) || "Add Channel"}
                </Button>
              </Stack>
            </Box>
            <Dialog
              open={!!pendingDelete}
              onClose={() => setPendingDelete(null)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle sx={{ fontSize: "18px" }}>
                Remove distribution channel?
              </DialogTitle>
              <DialogContent>
                <Typography sx={{ fontSize: "14px" }}>
                  {pendingDelete &&
                    `"${pendingDelete.label}" is currently used by ${
                      pendingDelete.oeCount
                    } OE${pendingDelete.oeCount === 1 ? "" : "s"} and has ${
                      pendingDelete.appCount
                    } app${
                      pendingDelete.appCount === 1 ? "" : "s"
                    } reported under it. Removing it will delete those apps. This cannot be undone.`}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setPendingDelete(null)}
                  sx={{ textTransform: "none" }}
                >
                  {getLocalizedMessage(language, "dialog.button.cancel") ||
                    "Cancel"}
                </Button>
                <Button
                  color="error"
                  onClick={confirmDeleteChannel}
                  sx={{ textTransform: "none" }}
                >
                  {getLocalizedMessage(language, "delete") || "Remove channel"}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        width: "100%",
        height: "calc(100vh - 84px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        p: 2,
        boxShadow: "none",
        gap: 2,
      }}
    >
      <Typography sx={{ fontSize: "24px", fontWeight: 500 }}>
        {getLocalizedMessage(language, "reportingUseCase.title") ||
          "Reporting Use Case"}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          color: grey[600],
        }}
      >
        {getLocalizedMessage(language, "reportingUseCase.description") ||
          "The Pimo reporting tool covers various use cases. The selection of the use case determines the structure of the reporting. Click on one of the use cases to find out more."}
      </Typography>
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: 500,
          textAlign: "left",
          width: "100%",
        }}
      >
        {getLocalizedMessage(language, "reportingUseCase.useCaseSelection") ||
          "Use Case Selection"}
      </Typography>
      <Paper
        sx={{
          p: 4,
          borderRadius: "12px",
          boxShadow: "none",
          border: "1px solid",
          borderColor: grey[300],
        }}
      >
        <Stack
          direction="row"
          mb={4}
          sx={{ display: "flex", justifyContent: "space-evenly" }}
        >
          <Button
            disabled
            onClick={() => setSelectedUseCase("pmo")}
            sx={{
              px: 4,
              py: 2,
              borderRadius: "15px",
              textTransform: "none",
              fontSize: "14px",
              bgcolor: selectedUseCase === "pmo" ? "primary.main" : undefined,
              color: selectedUseCase === "pmo" ? "#fff" : undefined,
              "&:hover": {
                bgcolor: selectedUseCase === "pmo" ? "primary.dark" : undefined,
              },
              border: "1px solid",
              borderColor: grey[700],
            }}
          >
            {getLocalizedMessage(
              language,
              "reportingUseCase.projectStatusReporting"
            ) || "Project Status Reporting"}
          </Button>
          <Button
            disabled
            onClick={() => setSelectedUseCase("rollout")}
            sx={{
              px: 4,
              py: 2,
              borderRadius: "12px",
              fontSize: "14px",
              textTransform: "none",
              bgcolor:
                selectedUseCase === "rollout" ? "primary.main" : undefined,
              color: selectedUseCase === "rollout" ? "#fff" : undefined,
              "&:hover": {
                bgcolor:
                  selectedUseCase === "rollout" ? "primary.dark" : undefined,
              },
              border: "1px solid",
              borderColor: grey[700],
            }}
          >
            {getLocalizedMessage(
              language,
              "reportingUseCase.rolloutStatusReporting"
            ) || "Rollout Status Reporting"}
          </Button>
          <Button
            disabled
            onClick={() => setSelectedUseCase("audit")}
            sx={{
              px: 4,
              py: 2,
              textTransform: "none",
              fontSize: "14px",
              borderRadius: "12px",
              bgcolor: selectedUseCase === "audit" ? "primary.main" : undefined,
              color: selectedUseCase === "audit" ? "#fff" : undefined,
              "&:hover": {
                bgcolor:
                  selectedUseCase === "audit" ? "primary.dark" : undefined,
              },
              border: "1px solid",
              borderColor: grey[700],
            }}
          >
            {getLocalizedMessage(
              language,
              "reportingUseCase.measureReportingForFindings"
            ) || "Measure Reporting for Findings"}
          </Button>
          <Button
            disabled
            onClick={() => setSelectedUseCase("assessment")}
            sx={{
              px: 4,
              py: 2,
              textTransform: "none",
              fontSize: "14px",
              borderRadius: "12px",
              bgcolor:
                selectedUseCase === "assessment" ? "primary.main" : undefined,
              color: selectedUseCase === "assessment" ? "#fff" : undefined,
              "&:hover": {
                bgcolor:
                  selectedUseCase === "assessment" ? "primary.dark" : undefined,
              },
              border: "1px solid",
              borderColor: grey[700],
            }}
          >
            Assessment
          </Button>
          <Button
            onClick={() => setSelectedUseCase("distribution")}
            sx={{
              px: 4,
              py: 2,
              textTransform: "none",
              fontSize: "14px",
              borderRadius: "12px",
              bgcolor:
                selectedUseCase === "distribution" ? "primary.main" : undefined,
              color: selectedUseCase === "distribution" ? "#fff" : undefined,
              "&:hover": {
                bgcolor:
                  selectedUseCase === "distribution"
                    ? "primary.dark"
                    : undefined,
              },
              border: "1px solid",
              borderColor: grey[700],
            }}
          >
            {getLocalizedMessage(language, "reportingUseCase.distribution") ||
              "Distribution"}
          </Button>
        </Stack>
        <Divider sx={{ mb: 4 }} />
        <Box>{renderContent()}</Box>
      </Paper>
    </Card>
  );
};
